import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Upload, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

const VISA_TYPES_UK = ["Skilled Worker", "Global Talent", "Health & Care Worker", "Student", "Graduate", "Dependent", "ILR", "HSMP", "British Citizen (OCI)", "Other"];
const VISA_TYPES_AU = ["482 TSS (Skills in Demand)", "189 Skilled Independent", "190 Skilled Nominated", "491 Regional", "500 Student", "485 Graduate", "Partner Visa", "Permanent Resident", "Australian Citizen (OCI)", "Other"];
const RELIGIONS = ["Hindu", "Muslim", "Sikh", "Christian", "Jain", "Buddhist", "Parsi", "Other"];
const MOTHER_TONGUES = ["Hindi", "Tamil", "Telugu", "Malayalam", "Kannada", "Marathi", "Gujarati", "Punjabi", "Bengali", "Urdu", "Odia", "Assamese", "Other"];
const CASTES = ["Brahmin", "Iyer", "Iyengar", "Nair", "Reddy", "Kamma", "Naidu", "Chettiar", "Mudaliar", "Pillai", "Vanniyar", "Gounder", "Ezhava", "Nadar", "Maratha", "Rajput", "Kshatriya", "Jat", "Patel", "Bania", "Agarwal", "Gupta", "Kayastha", "Khatri", "Sindhi", "Yadav", "Kurmi", "Vokkaliga", "Lingayat", "OBC", "SC/ST", "Christian", "Muslim", "Sikh", "Jain", "Parsi", "Other"];

function incomeCurrencyLabel(form) {
  if (form.member_type === "india") return "INR ₹";
  if (form.residence_country === "Australia") return "AUD $";
  return "GBP £";
}

const initial = {
  member_type: "nri",
  residence_country: "United Kingdom",
  email: "", mobile: "", password: "", full_name: "", gender: "", date_of_birth: "",
  religion: "", caste: "", mother_tongue: "", marital_status: "Never Married",
  height_cm: "", education: "", profession: "", company: "", annual_income_gbp: "",
  current_address: "", current_city: "", current_postcode: "",
  home_country: "India", home_country_address: "", home_country_city: "",
  father_name: "", mother_name: "", parents_phone: "",
  father_occupation: "", mother_occupation: "",
  visa_type: "", visa_expiry_date: "", visa_share_code: "",
  passport_number: "", passport_issue_country: "India", citizenship_certificate_number: "",
  about_me: "", partner_preference: "",
  profile_photo_path: "", gallery_paths: [],
};

export default function Register() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [visaConsent, setVisaConsent] = useState(false);
  const [dialCode, setDialCode] = useState("+44");
  const memberType = form.member_type;
  const isNri = memberType === "nri";
  const totalSteps = isNri ? 4 : 3;

  useEffect(() => {
    if (!isNri) setDialCode("+91");
    else setDialCode(form.residence_country === "Australia" ? "+61" : "+44");
  }, [memberType, form.residence_country]);
  const { login } = useAuth();
  const nav = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (file, kind) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      // register user first if not exists? No, uploads require auth. So we can't upload before register.
      // Trick: we accept photo file on the client, register user first, then upload.
      toast.error("Please complete registration first, then add photos from your dashboard.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const STEP_FIELDS = {
    1: ["email", "mobile", "password", "full_name", "gender", "date_of_birth", "height_cm", "marital_status"],
    2: ["religion", "caste", "mother_tongue", "education", "profession", "company", "annual_income_gbp"],
    3: ["current_address", "current_city", "current_postcode", "home_country", "home_country_city", "home_country_address", "father_name", "father_occupation", "mother_name", "mother_occupation", "parents_phone"],
    4: ["visa_type", "visa_expiry_date", "visa_share_code"],
  };

  const validateStep = (s) => {
    const fields = (s === 3 && !isNri)
      ? ["current_address", "current_city", "father_name", "father_occupation", "mother_name", "mother_occupation", "parents_phone"]
      : (s === 4 && form.residence_country === "Australia")
      ? ["visa_type", "visa_expiry_date", ...(form.visa_type.includes("Citizen") ? ["citizenship_certificate_number"] : ["passport_number", "passport_issue_country"])]
      : (STEP_FIELDS[s] || []);
    for (const k of fields) {
      if (!String(form[k] || "").trim()) return `Please fill in ${k.replace(/_/g, " ")}`;
    }
    if (s === 1) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email address";
      if (form.password.length < 8) return "Password must be at least 8 characters";
      if (!/^\+?[0-9\s()-]{7,18}$/.test(form.mobile)) return "Please enter a valid mobile number";
      const h = Number(form.height_cm);
      if (!h || h < 100 || h > 250) return "Height must be between 100 and 250 cm";
      if (new Date(form.date_of_birth) >= new Date()) return "Date of birth must be in the past";
    }
    if (s === 2) {
      const inc = Number(form.annual_income_gbp);
      if (isNaN(inc) || inc < 0) return "Annual income must be a valid number";
    }
    if (s === 4) {
      if (new Date(form.visa_expiry_date) < new Date()) return "Visa expiry date must be in the future";
      if (form.residence_country === "United Kingdom" && !/^[A-Za-z0-9]{9}$/.test(form.visa_share_code.trim())) return "Visa share code must be 9 letters/digits (e.g. A1B2C3D4E)";
    }
    return null;
  };

  const nextStep = () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    setStep((s) => s + 1);
  };

  const validate = () => {
    for (const s of [1, 2, 3, 4]) {
      if (s > totalSteps) break;
      const err = validateStep(s);
      if (err) return err;
    }
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!termsAccepted) { toast.error("Please accept the Terms & Privacy Policy to continue"); return; }
    if (isNri && !visaConsent) { toast.error("Please consent to visa verification to continue"); return; }
    setBusy(true);
    try {
      const payload = { ...form, mobile: `${dialCode}${form.mobile.replace(/[^0-9]/g, "")}`, height_cm: Number(form.height_cm) || 0, annual_income_gbp: Number(form.annual_income_gbp) || 0, terms_accepted: termsAccepted, visa_verification_consent: isNri ? visaConsent : false, member_type: memberType };
      if (!isNri) {
        payload.home_country = "India";
        payload.home_country_address = payload.home_country_address || payload.current_address;
        payload.home_country_city = payload.home_country_city || payload.current_city;
      }
      const r = await api.post("/auth/register", payload);
      login(r.data.token, r.data.user);
      if (isNri) {
        toast.success("Welcome! Your profile is live.");
        nav("/dashboard");
      } else {
        toast.success("India Resident account created — activate your India Resident £79 plan to start searching.");
        nav("/pricing");
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBusy(false); }
  };

  const progress = (step / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <h1 className="font-serif text-3xl sm:text-4xl font-semibold text-slate-900">Create your free profile</h1>
        <p className="text-slate-600 mt-1">All fields marked * are required — verified community.</p>
        <Progress value={progress} className="mt-6" />
        <p className="text-xs text-slate-500 mt-2">Step {step} of {totalSteps}</p>

        <Card className="mt-6 p-6 sm:p-8 border-[#EAE3D2] bg-white">
          {step === 1 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <h2 className="sm:col-span-2 font-serif text-2xl text-slate-900">Login & Personal</h2>
              <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="member-type-selector">
                <button type="button" data-testid="member-type-nri" onClick={() => set("member_type", "nri")}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${isNri ? "border-[#6B1D2F] bg-[#6B1D2F]/5" : "border-[#EAE3D2] bg-white hover:border-[#6B1D2F]/40"}`}>
                  <p className="font-semibold text-slate-900">I live abroad (NRI)</p>
                  <p className="text-xs text-slate-600 mt-1">UK or Australia — full profile with visa verification</p>
                </button>
                <button type="button" data-testid="member-type-india" onClick={() => set("member_type", "india")}
                  className={`p-4 rounded-xl border-2 text-left transition-colors ${!isNri ? "border-[#6B1D2F] bg-[#6B1D2F]/5" : "border-[#EAE3D2] bg-white hover:border-[#6B1D2F]/40"}`}>
                  <p className="font-semibold text-slate-900">I'm in India, seeking an NRI match</p>
                  <p className="text-xs text-slate-600 mt-1">India Resident profile — £79/6 months to search</p>
                </button>
              </div>
              {isNri && (
                <Field label="Country of residence *">
                  <Select value={form.residence_country} onValueChange={(v) => set("residence_country", v)}>
                    <SelectTrigger data-testid="reg-residence-country"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                      <SelectItem value="Australia">Australia</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
              <Field label="Email *"><Input data-testid="reg-email" value={form.email} onChange={(e) => set("email", e.target.value)} type="email" /></Field>
              <Field label="Mobile *">
                <div className="flex gap-2">
                  <Select value={dialCode} onValueChange={setDialCode}>
                    <SelectTrigger className="w-[104px]" data-testid="reg-dial-code"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="+44">UK +44</SelectItem>
                      <SelectItem value="+61">AU +61</SelectItem>
                      <SelectItem value="+91">IN +91</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input data-testid="reg-mobile" className="flex-1" value={form.mobile} onChange={(e) => set("mobile", e.target.value)} placeholder={dialCode === "+44" ? "7700 900123" : dialCode === "+61" ? "410 090 006" : "99009 00005"} />
                </div>
              </Field>
              <Field label="Password *"><Input data-testid="reg-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)} /></Field>
              <Field label="Full name *"><Input data-testid="reg-name" value={form.full_name} onChange={(e) => set("full_name", e.target.value)} /></Field>
              <Field label="Gender *">
                <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                  <SelectTrigger data-testid="reg-gender"><SelectValue placeholder="Select gender" /></SelectTrigger>
                  <SelectContent><SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
                </Select>
              </Field>
              <Field label="Date of birth *"><Input data-testid="reg-dob" type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} /></Field>
              <Field label="Height (cm) *"><Input data-testid="reg-height" type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} /></Field>
              <Field label="Marital status *">
                <Select value={form.marital_status} onValueChange={(v) => set("marital_status", v)}>
                  <SelectTrigger data-testid="reg-marital"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="Never Married">Never Married</SelectItem><SelectItem value="Divorced">Divorced</SelectItem><SelectItem value="Widowed">Widowed</SelectItem></SelectContent>
                </Select>
              </Field>
            </div>
          )}
          {step === 2 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <h2 className="sm:col-span-2 font-serif text-2xl text-slate-900">Community & Education</h2>
              <Field label="Religion *">
                <Select value={form.religion} onValueChange={(v) => set("religion", v)}>
                  <SelectTrigger data-testid="reg-religion"><SelectValue placeholder="Select religion" /></SelectTrigger>
                  <SelectContent>{RELIGIONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Caste / Community *">
                <Select value={form.caste} onValueChange={(v) => set("caste", v)}>
                  <SelectTrigger data-testid="reg-caste"><SelectValue placeholder="Select community" /></SelectTrigger>
                  <SelectContent>{CASTES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Mother tongue *">
                <Select value={form.mother_tongue} onValueChange={(v) => set("mother_tongue", v)}>
                  <SelectTrigger data-testid="reg-mt"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{MOTHER_TONGUES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Highest education *"><Input data-testid="reg-education" value={form.education} onChange={(e) => set("education", e.target.value)} /></Field>
              <Field label="Profession *"><Input data-testid="reg-profession" value={form.profession} onChange={(e) => set("profession", e.target.value)} /></Field>
              <Field label="Company *"><Input data-testid="reg-company" value={form.company} onChange={(e) => set("company", e.target.value)} /></Field>
              <Field label={`Annual income (${incomeCurrencyLabel(form)}) *`}><Input data-testid="reg-income" type="number" value={form.annual_income_gbp} onChange={(e) => set("annual_income_gbp", e.target.value)} placeholder={`Amount in ${incomeCurrencyLabel(form)}`} /></Field>
            </div>
          )}
          {step === 3 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <h2 className="sm:col-span-2 font-serif text-2xl text-slate-900">Residence & Family</h2>
              <Field label={`Current address (${isNri ? form.residence_country : "India"}) *`} className="sm:col-span-2"><Input data-testid="reg-addr" value={form.current_address} onChange={(e) => set("current_address", e.target.value)} /></Field>
              <Field label="City *"><Input data-testid="reg-city" value={form.current_city} onChange={(e) => set("current_city", e.target.value)} /></Field>
              <Field label={isNri ? "Postcode *" : "PIN code"}><Input data-testid="reg-postcode" value={form.current_postcode} onChange={(e) => set("current_postcode", e.target.value)} /></Field>
              {isNri && (
                <>
                  <Field label="Home country *"><Input data-testid="reg-home-country" value={form.home_country} onChange={(e) => set("home_country", e.target.value)} /></Field>
                  <Field label="Home country city *"><Input data-testid="reg-home-city" value={form.home_country_city} onChange={(e) => set("home_country_city", e.target.value)} /></Field>
                  <Field label="Home country address *" className="sm:col-span-2"><Input data-testid="reg-home-addr" value={form.home_country_address} onChange={(e) => set("home_country_address", e.target.value)} /></Field>
                </>
              )}
              <Field label="Father's name *"><Input data-testid="reg-father" value={form.father_name} onChange={(e) => set("father_name", e.target.value)} /></Field>
              <Field label="Father's occupation *"><Input data-testid="reg-father-occ" value={form.father_occupation} onChange={(e) => set("father_occupation", e.target.value)} /></Field>
              <Field label="Mother's name *"><Input data-testid="reg-mother" value={form.mother_name} onChange={(e) => set("mother_name", e.target.value)} /></Field>
              <Field label="Mother's occupation *"><Input data-testid="reg-mother-occ" value={form.mother_occupation} onChange={(e) => set("mother_occupation", e.target.value)} /></Field>
              <Field label="Parents' phone *"><Input data-testid="reg-parents-phone" value={form.parents_phone} onChange={(e) => set("parents_phone", e.target.value)} /></Field>
              {!isNri && (
                <>
                  <p className="sm:col-span-2 text-sm text-slate-500">Profile photo & gallery can be added after signup from your dashboard.</p>
                  <div className="sm:col-span-2 flex items-start gap-3 mt-2" data-testid="terms-consent-block">
                    <Checkbox id="terms" checked={termsAccepted} onCheckedChange={(v) => setTermsAccepted(!!v)} data-testid="terms-checkbox" className="mt-1" />
                    <label htmlFor="terms" className="text-sm text-slate-700 leading-snug cursor-pointer">
                      I confirm I am 18 or older and agree to the{" "}
                      <Link to="/terms" target="_blank" data-testid="terms-link" className="text-[#6B1D2F] font-semibold underline">Terms &amp; Conditions</Link>{" "}
                      and{" "}
                      <Link to="/privacy" target="_blank" data-testid="privacy-link" className="text-[#6B1D2F] font-semibold underline">Privacy Policy</Link>.
                      I expressly consent to the processing of my religion, caste/community and photographs as described in the Privacy Policy.
                    </label>
                  </div>
                </>
              )}
            </div>
          )}
          {step === 4 && isNri && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <h2 className="sm:col-span-2 font-serif text-2xl text-slate-900">Immigration & About</h2>
              <Field label="Visa type *">
                <Select value={form.visa_type} onValueChange={(v) => set("visa_type", v)}>
                  <SelectTrigger data-testid="reg-visa-type"><SelectValue placeholder="Select visa" /></SelectTrigger>
                  <SelectContent>{(form.residence_country === "Australia" ? VISA_TYPES_AU : VISA_TYPES_UK).map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Visa expiry date *"><Input data-testid="reg-visa-exp" type="date" value={form.visa_expiry_date} onChange={(e) => set("visa_expiry_date", e.target.value)} /></Field>
              {form.residence_country === "United Kingdom" ? (
                <Field label="Visa share code *" className="sm:col-span-2"><Input data-testid="reg-visa-share" value={form.visa_share_code} onChange={(e) => set("visa_share_code", e.target.value)} placeholder="Government share code" /></Field>
              ) : form.visa_type.includes("Citizen") ? (
                <>
                  <Field label="Citizenship certificate number *" className="sm:col-span-2"><Input data-testid="reg-cit-cert" value={form.citizenship_certificate_number} onChange={(e) => set("citizenship_certificate_number", e.target.value)} placeholder="Stock number on certificate (e.g. XXX/XXXXX)" /></Field>
                  <p className="sm:col-span-2 text-sm text-slate-600 bg-blue-50/60 border border-blue-200 rounded-lg p-3">Verified via the integrated government document verification service.</p>
                </>
              ) : (
                <>
                  <Field label="Passport number *"><Input data-testid="reg-passport" value={form.passport_number} onChange={(e) => set("passport_number", e.target.value)} /></Field>
                  <Field label="Passport country of issue *"><Input data-testid="reg-passport-country" value={form.passport_issue_country} onChange={(e) => set("passport_issue_country", e.target.value)} /></Field>
                  <p className="sm:col-span-2 text-sm text-slate-600 bg-blue-50/60 border border-blue-200 rounded-lg p-3" data-testid="au-vevo-note">
                    Verified via the integrated government immigration verification service using your passport details.
                  </p>
                </>
              )}
              <Field label="About me" className="sm:col-span-2"><Textarea data-testid="reg-about" rows={3} value={form.about_me} onChange={(e) => set("about_me", e.target.value)} /></Field>
              <Field label="Partner preference" className="sm:col-span-2"><Textarea data-testid="reg-partner" rows={3} value={form.partner_preference} onChange={(e) => set("partner_preference", e.target.value)} /></Field>
              <p className="sm:col-span-2 text-sm text-slate-500">Profile photo & gallery can be added after signup from your dashboard.</p>
              <div className="sm:col-span-2 flex items-start gap-3" data-testid="visa-consent-block">
                <Checkbox id="visa-consent" checked={visaConsent} onCheckedChange={(v) => setVisaConsent(!!v)} data-testid="visa-consent-checkbox" className="mt-1" />
                <label htmlFor="visa-consent" className="text-sm text-slate-700 leading-snug cursor-pointer">
                  I expressly consent to EliteNRIMatrimony verifying my immigration/visa details against official government verification services using the details I have provided, storing the verification outcome on my profile, and disclosing my visa verification details only to profiles I mutually accept an expression of interest with.
                </label>
              </div>
              <div className="sm:col-span-2 flex items-start gap-3 mt-2" data-testid="terms-consent-block">
                <Checkbox id="terms" checked={termsAccepted} onCheckedChange={(v) => setTermsAccepted(!!v)} data-testid="terms-checkbox" className="mt-1" />
                <label htmlFor="terms" className="text-sm text-slate-700 leading-snug cursor-pointer">
                  I confirm I am 18 or older and agree to the{" "}
                  <Link to="/terms" target="_blank" data-testid="terms-link" className="text-[#6B1D2F] font-semibold underline">Terms &amp; Conditions</Link>{" "}
                  and{" "}
                  <Link to="/privacy" target="_blank" data-testid="privacy-link" className="text-[#6B1D2F] font-semibold underline">Privacy Policy</Link>.
                  I expressly consent to the processing of my religion, caste/community and photographs as described in the Privacy Policy.
                </label>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 gap-3">
            <Button variant="ghost" onClick={() => setStep((s) => Math.max(1, s - 1))} disabled={step === 1} data-testid="reg-back">Back</Button>
            {step < totalSteps ? (
              <Button onClick={nextStep} data-testid="reg-next" className="bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full px-8">Continue</Button>
            ) : (
              <Button onClick={submit} disabled={busy} data-testid="reg-submit" className="bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full px-8">{busy ? "Creating…" : "Create profile"}</Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-slate-700 text-sm">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
